/**
 * ============================================================================
 * useDisposeFindingMutation — Phase 7 frontend
 * Owner: Phase 7 — Internal Audit Programme Module.
 * Purpose: POST /api/v1/audits/{id}/findings/{fid}/dispose
 *          INA-005 disposition: CAPA_OPENED or NO_ACTION_REQUIRED (≥30-char
 *          rationale) or WITHDRAWN. The server enforces the invariant; the
 *          UI just shapes the body and reports validation errors.
 * URS rows: INA-005, CAPA-016.
 * ============================================================================ */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { apiFetch, newIdempotencyKey } from '../../api/client';
import type { FindingDisposition, FindingItem } from '../AuditCalendar/types';

export interface DisposeFindingInput {
  auditId: string;
  findingId: string;
  disposition: FindingDisposition;
  noActionRationale?: string;
  responseSummary?: string;
}

export function useDisposeFindingMutation(): UseMutationResult<
  FindingItem,
  Error,
  DisposeFindingInput
> {
  const qc = useQueryClient();
  return useMutation<FindingItem, Error, DisposeFindingInput>({
    mutationFn: ({ auditId, findingId, disposition, noActionRationale, responseSummary }) =>
      apiFetch<FindingItem>(
        `/api/v1/audits/${encodeURIComponent(auditId)}/findings/${encodeURIComponent(findingId)}/dispose`,
        {
          method: 'POST',
          body: {
            disposition,
            no_action_rationale: noActionRationale ?? null,
            response_summary: responseSummary ?? null,
          },
          idempotencyKey: newIdempotencyKey(),
        },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['internal-audit', 'audit-detail', vars.auditId] });
    },
  });
}
